import { useState } from "react";
import { useParams } from "react-router-dom";
import { DateTime } from "luxon";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import { IApiRequestLogEntry, IApiTokenSummary } from "shared";

import { Spinner } from "../../../Common/Spinner";
import { Badge } from "../../../../../common/Atoms/Badge";
import { Button } from "../../../../../common/Atoms/Button";
import { FormLabel } from "../../../../../common/Atoms/FormLabel";
import { Paragraph } from "../../../../../common/Atoms/Typography/Paragraph";
import { Select } from "../../../../../common/Atoms/Select";
import { TwoColFormRow } from "../../../Molecules/Form/TwoColFormRow";
import { useFetchApiRequestLogsInfiniteQuery, useFetchApiTokensQuery } from "../../../../services/api/apiTokenApi/apiToken";

type Outcome = `success` | `error`;

const outcomeOptions = [
  { label: `All results`, value: `` },
  { label: `Success`, value: `success` },
  { label: `Error`, value: `error` },
];

function tokenLabel(token: IApiTokenSummary) {
  return `API token, created ${DateTime.fromISO(token.createdAt).toFormat(`yyyy-LL-dd`)}`;
}

function RequestRow({ request, token }: { request: IApiRequestLogEntry; token?: IApiTokenSummary }) {
  const [open, setOpen] = useState(false);
  const isError = request.status >= 400;
  const Chevron = open ? ChevronDownIcon : ChevronRightIcon;

  return (
    <div className={ `border-b border-gray-100` }>
      <div
        className={ `grid grid-cols-12 items-center py-2 cursor-pointer hover:bg-gray-50` }
        onClick={ () => setOpen(!open) }
      >
        <Paragraph
          className={ `col-span-3` }
          variant={ `secondary` }
        >
          { DateTime.fromISO(request.createdAt).toFormat(`yyyy-LL-dd HH:mm:ss`) }
        </Paragraph>
        <Paragraph className={ `col-span-3 truncate` }>
          { request.operationId }
        </Paragraph>
        <Paragraph
          className={ `col-span-3 truncate` }
          variant={ `secondary` }
        >
          { request.oauthClientName
            ? `${request.oauthClientName}, ${request.userName || `unknown user`}`
            : token ? tokenLabel(token) : `API token (revoked)` }
        </Paragraph>
        <div className={ `col-span-2` }>
          <Badge
            color={ isError ? `red` : `green` }
            message={ isError ? `${request.status} ${request.errorCode}` : `${request.status}` }
            className={ `inline-block` }
          />
        </div>
        <div className={ `col-span-1 flex justify-end` }>
          <Chevron className={ `h-5 w-5 text-gray-400` } />
        </div>
      </div>

      { open && (
        <div className={ `pb-3 space-y-2` }>
          <Paragraph variant={ `secondary` }>
            { `${request.source === `mcp` ? `MCP tool call` : `REST request`} · ${request.durationMs} ms · ${request.ip || `Unknown IP`}` }
          </Paragraph>
          { request.userAgent && (
            <Paragraph
              variant={ `secondary` }
              className={ `break-all` }
            >
              { request.userAgent }
            </Paragraph>
          ) }
          { request.errorMessage && (
            <Paragraph variant={ `error` }>
              { request.errorMessage }
            </Paragraph>
          ) }
          <pre className={ `text-xs bg-gray-50 rounded-md p-2 overflow-x-auto` }>
            { JSON.stringify(request.args ?? {}, null, 2) }
          </pre>
        </div>
      ) }
    </div>
  );
}

export function ApiRequestLog() {
  const { organisationId } = useParams();
  const [tokenId, setTokenId] = useState(``);
  const [outcome, setOutcome] = useState<Outcome | ``>(``);

  const { data: tokenData } = useFetchApiTokensQuery(organisationId, {
    skip: !organisationId,
  });
  const {
    data,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useFetchApiRequestLogsInfiniteQuery({
    organisationId,
    tokenId: tokenId || undefined,
    outcome: outcome || undefined,
  }, {
    skip: !organisationId,
  });

  const tokens = tokenData?.tokens ?? [];
  const tokensById = new Map(tokens.map(token => [ token.id, token ]));
  const requests = data?.pages.flatMap(page => page.requests) ?? [];

  const tokenOptions = [
    { label: `All tokens`, value: `` },
    ...tokens.map(token => ({ label: tokenLabel(token), value: token.id })),
  ];

  return (
    <TwoColFormRow
      heading={ `Request Log` }
      description={ `Every public API request and MCP tool call from the last 90 days.` }
    >
      <div className={ `space-y-3` }>
        { /* Select's wrapper is min-w-full, so it sizes from a grid cell, not its own width class */ }
        <div className={ `grid grid-cols-3 gap-3` }>
          <Select
            className={ `col-span-2` }
            options={ tokenOptions }
            selected={ tokenId }
            onChange={ ({ value }) => setTokenId(value) }
          />
          <Select
            options={ outcomeOptions }
            selected={ outcome }
            onChange={ ({ value }) => setOutcome(value) }
          />
        </div>

        { isLoading ? (
          <Spinner />
        ) : requests.length === 0 ? (
          <Paragraph variant={ `secondary` }>
            { `No requests yet.` }
          </Paragraph>
        ) : (
          <div>
            <div className={ `grid grid-cols-12 border-b border-gray-200 pb-1` }>
              <FormLabel className={ `col-span-3` }>{ `Time` }</FormLabel>
              <FormLabel className={ `col-span-3` }>{ `Operation` }</FormLabel>
              <FormLabel className={ `col-span-3` }>{ `Token / app` }</FormLabel>
              <FormLabel className={ `col-span-2` }>{ `Result` }</FormLabel>
            </div>
            { requests.map(request => (
              <RequestRow
                key={ request.id }
                request={ request }
                token={ tokensById.get(request.apiTokenId) }
              />
            )) }
          </div>
        ) }

        { hasNextPage && (
          <Button
            color={ `gray` }
            onClick={ () => fetchNextPage() }
            loading={ isFetchingNextPage }
          >
            { `Load more` }
          </Button>
        ) }
      </div>
    </TwoColFormRow>
  );
}
